import { queries } from "@msuite/zero";
import { useZeroQuery } from "@msuite/zero/react";
import { useQuery } from "@msuite/ui/query";
import { responseHandler } from "@msuite/ui/utils";
import type { Nullable } from "@msuite/utils/types";
import { client } from "@/hono";

/** Props Interface */
interface UseVideoProps {
	videoId: Nullable<string>;
}

/** The tokens last twelve hours, so asking again inside eleven signs the same claims twice. */
const PLAYBACK_TOKEN_STALE_MS = 11 * 60 * 60 * 1000;

/**
 * One video, live.
 *
 * The row arrives over the sync path rather than by asking, because nothing the browser does moves
 * it: Mux tells the server the encode is finished, the server writes the row, and the page would
 * never hear. Before this the block asked every 2.5 seconds for as long as a recording ran — some
 * seven hundred requests for a half-hour tutorial, none of which could have learned anything until
 * the last one.
 */
export function useVideo({ videoId }: UseVideoProps) {
	/** Sync */
	const [video, details] = useZeroQuery(videoId ? queries.videos.byId({ videoId }) : false);

	/** Computed */
	const isReady = video?.status === "ready";
	const isPending = video?.status === "uploading" || video?.status === "processing";

	/** Query */
	// The one thing still fetched. A playback token is minted per request and stored in no column,
	// so it cannot ride the sync path — and it is only wanted once the row says there is something
	// to play. The response carries the row as well; that half is ignored, because two sources for
	// one thing is how they start disagreeing.
	const playback = useQuery({
		// The playback id is part of the key, so a cut that repoints the row fetches a token for the
		// asset the row now names. Keyed on the video alone, an eleven-hour staleTime meant a reader
		// with the page already open went on streaming the pre-cut asset — which, when the author
		// kept the original, is the recording with the part they removed still in it.
		queryKey: ["video-playback", videoId ?? "--", video?.muxPlaybackId ?? "--"],
		queryFn: async () => {
			if (!videoId) throw new Error("No video ID");
			const response = await client.videos[":videoId"].$get({ param: { videoId } });
			const data = await responseHandler(response, false);
			return data.playback ?? null;
		},
		enabled: !!videoId && isReady,
		retry: false,
		staleTime: PLAYBACK_TOKEN_STALE_MS,
	});

	/** Query */
	// Development only, and not a read: it is the poke that stands in for the webhook. Mux cannot
	// reach `localhost`, so `Videos.getVideo` refreshes the row against the Mux API when somebody
	// reads it — and with nobody reading, a locally recorded video stays `uploading` for ever. Its
	// answer is discarded; what matters is the write it provokes, which then arrives over Zero
	// exactly as the webhook's would in production.
	useQuery({
		queryKey: ["video-mux-poke", videoId ?? "--"],
		queryFn: async () => {
			if (!videoId) return null;
			await client.videos[":videoId"].$get({ param: { videoId } });
			return null;
		},
		enabled: Boolean(import.meta.env.DEV) && !!videoId && isPending,
		refetchInterval: 2500,
		retry: false,
	});

	/** Return */
	return {
		video: video ?? null,
		playback: playback.data ?? null,
		// A row that is ready but whose tokens are still in flight is loading, not processing —
		// the block would otherwise say the encode is running when it has already finished.
		isLoading: details.type === "unknown" || (isReady && playback.isLoading),
		isError: details.type === "error",
	};
}
